import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Phone, Award, Briefcase, Clock, UserCheck, BadgeCheck, HeartHandshake, Building2, Home, Thermometer, Hammer, Layers, Beef, Quote, MapPin, Star, Sparkles, CheckCircle2 } from 'lucide-react';
import { COMPANY, SERVICES, QUALITY_POINTS, REFERENCES, TESTIMONIALS } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { SERVICE_TRANSLATIONS, QUALITY_TRANSLATIONS } from '../i18n/translations';
import { Reveal } from '../hooks/useReveal';
import GoogleLogo from '../components/GoogleLogo';
import AAStamp from '../components/AAStamp';
import AnimatedCounter from '../components/AnimatedCounter';
import MagneticButton from '../components/MagneticButton';
import InstagramSection from '../components/InstagramSection';
import SEO, { localBusinessLd } from '../components/SEO';

const iconMap = { Award, Briefcase, Clock, UserCheck, BadgeCheck, HeartHandshake, Building2, Home, Thermometer, Hammer, Layers, Beef, Sparkles };

const HomePage = () => {
  const { t, lang } = useT();
  const featuredRefs = REFERENCES.slice(0, 5);

  // Parallax hero image
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const heroImgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);

  // Mouse-tracking spotlight for hero
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  return (
    <div className="bg-white overflow-x-hidden">
      <SEO
        title="Kundenmaurer, Plattenleger & HardRock in Seelisberg · Uri"
        description="Frachigers Handwerk – Armin Achermann aus Seelisberg (Uri). Kundenmaurer, Plattenleger, HardRock fugenlose Oberflächen und Ab-Hof-Verkauf. AA+ Qualität, seit über 20 Jahren persönlich und zuverlässig in der Zentralschweiz."
        path="/"
        lang={lang}
        jsonLd={[localBusinessLd()]}
      />
      {/* ═══════════ HERO ═══════════ */}
      <section
        ref={heroRef}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setMouse({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
        }}
        className="relative min-h-[100vh] flex items-center overflow-hidden bg-white pt-28"
        data-testid="hero-section"
      >
        {/* Mouse-tracking spotlight */}
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background: `radial-gradient(600px circle at ${mouse.x * 100}% ${mouse.y * 100}%, rgba(30,90,168,.15), transparent 60%)`
          }}
        ></div>

        {/* Grid pattern background */}
        <div
          className="absolute inset-0 opacity-[0.25] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(#DCE8F7 1px, transparent 1px), linear-gradient(90deg, #DCE8F7 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        ></div>

        {/* Floating decorative blobs */}
        <div className="absolute top-24 -right-32 w-[520px] h-[520px] bg-brand-blueLight/25 rounded-full blur-3xl animate-floatY"></div>
        <div className="absolute bottom-0 -left-32 w-[520px] h-[520px] bg-brand-blue/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-brand-blueMist/60 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left: text */}
            <Reveal direction="right" className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 mb-6 bg-white border border-brand-blue/25 px-4 py-2 rounded-full shadow-sm shadow-brand-blue/10">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-blue opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-blue"></span>
                </span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-brand-blueDark font-bold">{t('hero.eyebrow')}</span>
              </div>

              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-brand-blueDark leading-[1.02] mb-4 tracking-tight">
                Frachigers
                <br />
                <span className="text-gradient-blue">Handwerk</span>
                <br />
                <span className="italic text-brand-blue glow-blue">AA+ Qualität.</span>
              </h1>

              <p className="text-xl md:text-2xl font-display text-brand-blueDark/70 italic mb-6">
                Kundenmaurer · Plattenleger · HardRock · Ab Hof · Seelisberg · Uri.
              </p>
              <p className="text-base md:text-lg text-brand-ink/70 max-w-xl mb-10 leading-relaxed">
                {t('hero.desc')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-14">
                <MagneticButton>
                  <Link
                    to="/kontakt"
                    className="group inline-flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blueDark text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-all shadow-xl shadow-brand-blue/30 btn-pulse-blue"
                    data-testid="hero-cta-primary"
                  >
                    {t('hero.cta1')}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </MagneticButton>
                <Link
                  to="/referenzen"
                  className="group inline-flex items-center justify-center gap-2 bg-white hover:bg-brand-beige border-2 border-brand-blueDark text-brand-blueDark px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors"
                  data-testid="hero-cta-secondary"
                >
                  {t('hero.cta2')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Stat trio */}
              <div className="grid grid-cols-3 gap-4 max-w-xl">
                {[
                  { icon: Award, val: '20+', k: 'experience' },
                  { icon: Star, val: '5.0', k: 'fair' },
                  { icon: Clock, val: '100%', k: 'punctual' },
                ].map(({ icon: Icon, val, k }, i) => (
                  <div key={k} className="flex flex-col gap-2">
                    <div className="w-11 h-11 rounded-sm bg-brand-blue text-white flex items-center justify-center shadow-lg shadow-brand-blue/20">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="font-display text-3xl text-brand-blueDark leading-none">
                      <AnimatedCounter value={val} />
                    </div>
                    <div className="text-brand-ink/60 text-[10px] uppercase tracking-widest font-bold">{t(`hero.${k}Sub`)}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Right: image with parallax + AA stamp */}
            <Reveal direction="left" delay={200} className="lg:col-span-5">
              <div className="relative">
                {/* Glow frame */}
                <div className="absolute -inset-4 bg-gradient-to-br from-brand-blueLight via-brand-blueMist to-brand-blue rounded-md blur-2xl opacity-50"></div>

                <div className="relative aspect-[4/5] rounded-md overflow-hidden shadow-2xl shadow-brand-blueDark/30">
                  <motion.img
                    src={COMPANY.heroImage}
                    alt="Frachigers Handwerk"
                    className="w-full h-full object-cover"
                    style={{ y: heroImgY, scale: heroImgScale }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-blueDark/60 via-transparent to-transparent"></div>

                  {/* Bottom company badge */}
                  <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-5 rounded-sm shadow-xl border border-brand-blueMist">
                    <div className="flex items-center gap-3">
                      <img src={COMPANY.logo} alt="Logo" className="h-14 w-14 object-contain" />
                      <div>
                        <div className="font-display text-xl text-brand-blueDark leading-tight">Frachigers Handwerk</div>
                        <div className="text-[10px] uppercase tracking-widest text-brand-blue font-bold">Armin Achermann · Seelisberg</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <div className="hidden md:block absolute -top-6 -right-6 z-20">
                  <AAStamp size={120} />
                </div>

                <div className="hidden md:flex absolute -bottom-6 -left-6 bg-brand-blueDark text-white p-4 rounded-sm shadow-2xl items-center gap-3 animate-floatY">
                  <Phone className="w-5 h-5 text-brand-blueSky" />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-70">{t('common.direct')}</div>
                    <a href={`tel:${COMPANY.phoneInternational}`} className="font-bold text-sm" data-testid="hero-phone">{COMPANY.phone}</a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* Marquee band */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden bg-brand-beige border-y border-brand-blueMist py-4">
          <div className="marquee-track">
            {[...Array(2)].flatMap((_, k) => [1, 2, 3, 4].map((n) => (
              <span key={`${k}-${n}`} className="font-display text-lg md:text-xl text-brand-blueDark italic mx-8 flex items-center gap-6 whitespace-nowrap">
                Frachigers Handwerk
                <span className="text-brand-blue text-2xl">✦</span>
                Kundenmaurer
                <span className="text-brand-blueLight text-2xl">✦</span>
                Plattenleger
                <span className="text-brand-blue text-2xl">✦</span>
                Ab Hof · BIO
                <span className="text-brand-blueLight text-2xl">✦</span>
                20+ Jahre Erfahrung
                <span className="text-brand-blue text-2xl">✦</span>
              </span>
            )))}
          </div>
        </div>
      </section>

      {/* ═══════════ SERVICES BENTO ═══════════ */}
      <section className="py-28 md:py-36 px-4 sm:px-6 lg:px-8 bg-white relative" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-14">
              <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('services.eyebrow')}</div>
              <h2 className="font-display text-4xl md:text-6xl text-brand-blueDark leading-[1.05] mb-6 tracking-tight">
                Unsere <span className="italic text-brand-blue">Dienstleistungen</span>
              </h2>
              <p className="text-brand-ink/70 text-lg leading-relaxed">{t('services.subtitle')}</p>
            </div>
          </Reveal>

          {/* Balanced grid — scales for 3-4 services */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = iconMap[s.icon] || Building2;
              const st = SERVICE_TRANSLATIONS[s.id]?.[lang] || {};
              return (
                <Reveal key={s.id} delay={i * 120}>
                  <Link
                    to={`/dienstleistungen/${s.slug}`}
                    className={`group relative block overflow-hidden rounded-md bg-brand-beige border border-brand-blueMist hover:border-brand-blue transition-all lift-on-hover h-full`}
                    data-testid={`service-card-${s.slug}`}
                  >
                    <div className="relative w-full h-full min-h-[380px] overflow-hidden">
                      <img
                        src={s.image}
                        alt={st.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-blueDark/95 via-brand-blueDark/40 to-transparent"></div>
                      <div className="absolute top-5 right-5 font-display text-5xl text-white/80 leading-none">
                        0{i + 1}
                      </div>
                      <div className="absolute top-5 left-5 w-14 h-14 rounded-md bg-white text-brand-blueDark flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all">
                        <Icon className="w-7 h-7" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h3 className="font-display text-2xl text-white mb-2 leading-tight">{st.title}</h3>
                        <p className="text-white/85 text-sm leading-relaxed mb-4 line-clamp-2">{st.description}</p>
                        <span className="inline-flex items-center gap-2 text-brand-blueSky font-bold text-xs uppercase tracking-[0.2em] group-hover:gap-4 transition-all">
                          {t('common.more')}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={300}>
            <div className="mt-14 text-center">
              <MagneticButton>
                <Link
                  to="/dienstleistungen"
                  className="inline-flex items-center gap-2 bg-brand-blueDark hover:bg-brand-blue text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors shadow-xl shadow-brand-blueDark/30 btn-pulse-blue"
                  data-testid="services-all-btn"
                >
                  {t('services.allBtn')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ FOUNDER / TRUST — Dark blue section ═══════════ */}
      <section className="relative py-28 md:py-36 px-4 sm:px-6 lg:px-8 bg-brand-blueDark text-white overflow-hidden bg-grain" data-testid="founder-section">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] bg-brand-blueLight/25 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] bg-brand-blue/30 rounded-full blur-3xl"></div>

        <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal direction="right">
            <div>
              <div className="text-brand-blueSky uppercase tracking-[0.3em] text-xs mb-4 font-bold">Der Meister hinter AA+</div>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mb-6 tracking-tight">
                Armin <span className="italic text-brand-blueSky">Achermann.</span>
                <br />
                <span className="text-brand-blueLight">Ihr persönlicher</span> Handwerker.
              </h2>
              <p className="text-white/85 text-lg leading-relaxed mb-6">
                Seit über 20 Jahren erschaffe ich mit Herzblut, Präzision und Schweizer Handwerkstradition. Als Einmannbetrieb sind Sie bei mir keine Nummer – Sie erhalten den Chef persönlich vom ersten Anruf bis zur Fertigstellung.
              </p>
              <p className="text-white/70 leading-relaxed mb-10">
                Vom kunstvollen Bruchsteinmauerwerk bis zur millimetergenauen Plattenverlegung – jedes Projekt trägt mein persönliches AA+ Qualitätssiegel.
              </p>

              <div className="grid grid-cols-3 gap-6 mb-10">
                {[
                  { val: '20+', label: 'Jahre' },
                  { val: '500+', label: 'Projekte' },
                  { val: '100%', label: 'Zufrieden' },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="font-display text-4xl md:text-5xl text-brand-blueSky leading-none mb-2">
                      <AnimatedCounter value={s.val} />
                    </div>
                    <div className="text-xs uppercase tracking-widest text-white/60 font-bold">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-3">
                <MagneticButton>
                  <a
                    href={`tel:${COMPANY.phoneInternational}`}
                    className="inline-flex items-center gap-2 bg-white text-brand-blueDark hover:bg-brand-blueSky px-7 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors shadow-xl"
                    data-testid="founder-call-btn"
                  >
                    <Phone className="w-4 h-4" />
                    {COMPANY.phone}
                  </a>
                </MagneticButton>
                <Link
                  to="/ueber-uns"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-white/40 hover:border-white text-white px-7 py-4 text-sm font-bold uppercase tracking-wider rounded-sm transition-colors"
                >
                  Über mich
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>

          <Reveal direction="left" delay={200}>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-brand-blueSky/30 to-brand-blue/20 rounded-md blur-xl"></div>
              <div className="relative aspect-[4/5] rounded-md overflow-hidden shadow-2xl">
                <img
                  src={REFERENCES[10].image}
                  alt="Armin Achermann bei der Arbeit"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blueDark/60 via-transparent to-transparent"></div>

                <div className="absolute bottom-6 left-6 right-6">
                  <div className="font-display text-3xl italic text-white leading-tight" style={{ fontFamily: '"Dancing Script", cursive' }}>
                    Armin Achermann
                  </div>
                  <div className="text-brand-blueSky text-xs uppercase tracking-[0.3em] font-bold mt-1">
                    Kundenmaurer · Plattenleger
                  </div>
                </div>
              </div>

              {/* Quality quote card */}
              <div className="hidden md:block absolute -bottom-8 -left-8 bg-white text-brand-blueDark p-6 rounded-md shadow-2xl max-w-xs">
                <Quote className="w-6 h-6 text-brand-blue mb-3" />
                <p className="text-sm italic leading-relaxed font-medium">
                  Zufriedene Kunden sind unser Antrieb und unsere Motivation.
                </p>
                <div className="mt-3 text-[10px] uppercase tracking-widest text-brand-blueLight font-bold">AA+ Versprechen</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════ QUALITY POINTS ═══════════ */}
      <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-brand-beige relative overflow-hidden" data-testid="quality-section">
        <div className="absolute top-0 -right-32 w-[400px] h-[400px] bg-brand-blueLight/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('quality.eyebrow')}</div>
              <h2 className="font-display text-4xl md:text-6xl text-brand-blueDark leading-tight mb-6 tracking-tight">
                {t('quality.title')} <span className="italic text-brand-blue">{t('quality.titleEm')}</span>
              </h2>
              <p className="text-brand-ink/70 text-lg leading-relaxed">{t('quality.subtitle')}</p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {QUALITY_POINTS.map((q, i) => {
              const Icon = iconMap[q.icon] || Award;
              const qt = QUALITY_TRANSLATIONS[q.key]?.[lang] || {};
              return (
                <Reveal key={q.id} delay={i * 80}>
                  <div className="relative bg-white border border-brand-blueMist hover:border-brand-blue rounded-md p-8 h-full lift-on-hover group overflow-hidden">
                    {/* Corner accent */}
                    <div className="absolute -top-10 -right-10 w-24 h-24 bg-brand-blueMist/60 rounded-full group-hover:scale-150 transition-transform duration-500"></div>

                    <div className="relative flex items-start justify-between mb-5">
                      <div className="w-14 h-14 rounded-md bg-brand-blue text-white flex items-center justify-center shadow-lg shadow-brand-blue/20 group-hover:scale-110 group-hover:-rotate-6 transition-all">
                        <Icon className="w-7 h-7" />
                      </div>
                      <span className="font-display text-5xl text-brand-blueMist">{q.id}</span>
                    </div>
                    <h3 className="relative font-display text-2xl text-brand-blueDark mb-3">{qt.title}</h3>
                    <p className="relative text-sm leading-relaxed text-brand-ink/70">{qt.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ REFERENCES TEASER (bento) ═══════════ */}
      <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-white" data-testid="references-teaser">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
              <div className="max-w-2xl">
                <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('refs.eyebrow')}</div>
                <h2 className="font-display text-4xl md:text-6xl text-brand-blueDark leading-tight tracking-tight">
                  <span className="text-brand-blue">{t('refs.title1')}</span>
                  <br />
                  <span className="italic">{t('refs.title2')}</span>
                </h2>
              </div>
              <Link to="/referenzen" className="group inline-flex items-center gap-2 text-brand-blueDark hover:text-brand-blue font-bold text-sm uppercase tracking-wider" data-testid="ref-teaser-link">
                {t('services.allReferences')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {featuredRefs.map((r, i) => (
              <Reveal key={r.id} delay={i * 70}>
                <Link
                  to="/referenzen"
                  className={`group relative block overflow-hidden rounded-md lift-on-hover shadow-sm bg-brand-beige ${
                    i === 0 ? 'md:col-span-2 md:row-span-2 h-64 md:h-full' : 'h-56 md:h-72'
                  }`}
                  data-testid={`ref-teaser-${r.id}`}
                >
                  <img src={r.image} alt={r.titleKey} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-blueDark/85 via-brand-blueDark/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                    <div className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.2em] mb-2 font-bold bg-white text-brand-blueDark">
                      {SERVICE_TRANSLATIONS[r.category]?.[lang]?.short}
                    </div>
                    <div className="text-white font-display text-base md:text-lg leading-tight">{r.titleKey}</div>
                    <div className="text-brand-blueSky text-[10px] uppercase tracking-widest mt-1">{r.location}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ TESTIMONIALS ═══════════ */}
      <section className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-brand-beige" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="text-brand-blueLight uppercase tracking-[0.3em] text-xs mb-3 font-bold">{t('testimonials.eyebrow')}</div>
              <h2 className="font-display text-4xl md:text-5xl text-brand-blueDark leading-tight tracking-tight">
                {t('testimonials.title')} <span className="italic text-brand-blue">{t('testimonials.titleEm')}</span>
              </h2>

              <a
                href={COMPANY.googleReviews}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 mt-8 bg-white border border-brand-blueMist px-5 py-3 rounded-sm hover:border-brand-blue hover:shadow-md transition-all group"
                data-testid="testimonials-google-link"
              >
                <GoogleLogo className="w-6 h-6" />
                <div className="text-left">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]" />
                    ))}
                  </div>
                  <div className="text-xs text-brand-ink/70 group-hover:text-brand-blueDark">{t('testimonials.google')}</div>
                </div>
              </a>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((tm, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="relative bg-white border-t-4 border-brand-blue border-x border-b border-brand-blueMist p-8 rounded-md hover:shadow-xl hover:shadow-brand-blue/10 transition-all h-full lift-on-hover">
                  <div className="flex items-center justify-between mb-5">
                    <Quote className="w-10 h-10 text-brand-blueMist" />
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((j) => (
                        <Star key={j} className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]" />
                      ))}
                    </div>
                  </div>
                  <p className="text-brand-ink/85 leading-relaxed mb-6 italic">«{tm.textKey[lang]}»</p>
                  <div className="border-t border-brand-blueMist pt-4 flex items-center justify-between">
                    <div>
                      <div className="font-bold text-brand-blueDark">{tm.nameKey}</div>
                      <div className="text-xs text-brand-ink/60">{tm.roleKey[lang]}</div>
                    </div>
                    <GoogleLogo className="w-5 h-5 opacity-60" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ INSTAGRAM ═══════════ */}
      <InstagramSection />

      {/* ═══════════ FINAL CTA — Dark blue drama ═══════════ */}
      <section className="relative py-28 md:py-36 px-4 sm:px-6 lg:px-8 bg-brand-blueDark text-white overflow-hidden bg-grain" data-testid="final-cta-section">
        <div className="absolute top-0 -right-32 w-[600px] h-[600px] bg-brand-blueLight/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -left-32 w-[600px] h-[600px] bg-brand-blue/30 rounded-full blur-3xl"></div>

        <Reveal>
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 mb-6 bg-white/10 border border-white/20 backdrop-blur px-4 py-2 rounded-full">
              <Sparkles className="w-3.5 h-3.5 text-brand-blueSky" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-brand-blueSky font-bold">Kostenlose Beratung</span>
            </div>
            <h2 className="font-display text-4xl md:text-7xl mb-6 leading-[1.05] tracking-tight">
              {t('cta.title')} <span className="italic text-brand-blueSky">{t('cta.titleEm')}</span>?
            </h2>
            <p className="text-white/85 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">{t('cta.subtitle')}</p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <MagneticButton>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 bg-white text-brand-blueDark hover:bg-brand-blueSky px-8 py-5 text-sm font-bold uppercase tracking-wider rounded-sm shadow-2xl transition-colors"
                  data-testid="final-cta-primary"
                >
                  {t('cta.request')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </MagneticButton>
              <MagneticButton>
                <a
                  href={`tel:${COMPANY.phoneInternational}`}
                  className="inline-flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blueLight text-white px-8 py-5 text-sm font-bold uppercase tracking-wider rounded-sm shadow-2xl transition-colors btn-pulse-blue"
                  data-testid="final-cta-phone"
                >
                  <Phone className="w-4 h-4" />
                  {COMPANY.phone}
                </a>
              </MagneticButton>
            </div>

            <div className="mt-10 inline-flex items-center gap-3 text-white/70 text-sm">
              <MapPin className="w-4 h-4 text-brand-blueSky" />
              {COMPANY.address.full}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default HomePage;
